import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ name: '', rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/testimonials`);
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      // Fallback to default testimonials if API fails
      setTestimonials([
        {
          review_id: '1',
          name: 'María García',
          rating: 5,
          comment: '¡Increíble transformación en solo 3 meses! Las rutinas son perfectas y el plan nutricional muy fácil de seguir. Deisy y Karen son las mejores entrenadoras.',
          featured: true
        },
        {
          review_id: '2',
          name: 'Carolina Mendoza',
          rating: 5,
          comment: 'El programa Premium vale cada centavo. Los resultados en mis glúteos y piernas han sido espectaculares. ¡Totalmente recomendado!',
          featured: true
        },
        {
          review_id: '3',
          name: 'Ana Rodríguez',
          rating: 5,
          comment: 'Me encanta la variedad de ejercicios y los diferentes menús del plan nutricional. He perdido 8kg y me siento más fuerte que nunca.',
          featured: false
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!newReview.comment.trim()) {
      alert('Por favor escribe un comentario');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/reviews`, newReview);
      alert('¡Gracias por tu opinión! Tu testimonio será revisado y publicado pronto.');
      setNewReview({ name: '', rating: 5, comment: '' });
      setShowReviewForm(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onChange && onChange(star)}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}
            disabled={!interactive}
          >
            <Star 
              className={`w-5 h-5 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} 
            />
          </button>
        ))}
      </div>
    );
  };

  // Separate featured and regular testimonials
  const featuredTestimonials = testimonials.filter(t => t.featured);
  const regularTestimonials = testimonials.filter(t => !t.featured);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-pink-500/10 text-pink-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            TESTIMONIOS
          </span>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            Historias Reales de <span className="text-pink-500">Transformación</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Descubre lo que nuestras alumnas dicen sobre su experiencia con Booty & Legs Academy
          </p>
        </div>

        {/* Featured Testimonials */}
        {featuredTestimonials.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-2">
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              Testimonios Destacados
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredTestimonials.slice(0, 4).map((testimonial) => (
                <div 
                  key={testimonial.review_id} 
                  className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-3xl p-8 border border-pink-500/20 relative overflow-hidden"
                >
                  <Quote className="absolute top-4 right-4 w-12 h-12 text-pink-500/20" />
                  <div className="mb-4">{renderStars(testimonial.rating)}</div>
                  <p className="text-zinc-300 text-lg mb-6 relative z-10">"{testimonial.comment}"</p>
                  <div className="flex items-center gap-4">
                    {testimonial.image_url ? (
                      <img 
                        src={testimonial.image_url} 
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center">
                        <span className="text-pink-400 font-bold text-lg">
                          {testimonial.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-white">{testimonial.name}</p>
                      <p className="text-sm text-pink-400">Alumna verificada</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Regular Testimonials Grid */}
        {regularTestimonials.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-8">Más Testimonios</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {regularTestimonials.map((testimonial) => (
                <div 
                  key={testimonial.review_id} 
                  className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 hover:border-pink-500/30 transition-colors"
                >
                  <div className="mb-3">{renderStars(testimonial.rating)}</div>
                  <p className="text-zinc-400 mb-4">"{testimonial.comment}"</p>
                  <p className="font-bold text-white">- {testimonial.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {testimonials.length === 0 && (
          <div className="text-center py-16">
            <Quote className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 text-lg">Aún no hay testimonios. ¡Sé la primera en compartir tu experiencia!</p>
          </div>
        )}

        {/* Submit Review Section */}
        <div className="bg-zinc-900/50 rounded-3xl p-8 md:p-12 border border-zinc-800 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            ¿Eres parte de nuestra comunidad?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
            Comparte tu experiencia y ayuda a otras mujeres a tomar la decisión de transformar su vida
          </p>
          
          {!showReviewForm ? (
            <Button 
              onClick={() => setShowReviewForm(true)}
              className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8 py-6 text-lg font-bold"
            >
              Dejar mi Testimonio
            </Button>
          ) : (
            <form onSubmit={submitReview} className="max-w-lg mx-auto text-left space-y-6">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Tu nombre</label>
                <input
                  type="text"
                  value={newReview.name}
                  onChange={(e) => setNewReview({...newReview, name: e.target.value})}
                  placeholder="María García"
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder:text-zinc-500 focus:border-pink-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Tu calificación</label>
                <div className="flex gap-2">
                  {renderStars(newReview.rating, true, (rating) => setNewReview({...newReview, rating}))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Tu experiencia *</label>
                <textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                  placeholder="Cuéntanos cómo ha sido tu experiencia con Booty & Legs Academy..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder:text-zinc-500 focus:border-pink-500 focus:outline-none resize-none"
                  required
                />
              </div>
              
              <div className="flex gap-4">
                <Button 
                  type="button"
                  onClick={() => setShowReviewForm(false)}
                  variant="outline"
                  className="flex-1 rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-pink-600 hover:bg-pink-500 text-white rounded-full"
                >
                  {submitting ? 'Enviando...' : 'Enviar Testimonio'}
                </Button>
              </div>
              
              <p className="text-xs text-zinc-500 text-center">
                Tu testimonio será revisado antes de ser publicado
              </p>
            </form>
          )}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-zinc-400 mb-6">¿Lista para comenzar tu transformación?</p>
          <a href="/programs">
            <Button className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-12 py-6 text-lg font-bold">
              Ver Programas
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
